import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import FilePreviewComp from 'components/FilePreview';
import { MoreDropdown } from 'router/subrouters/Dash/subrouters/private/subrouters/Portfolio/components/MoreDropdown';
import MessageFilePreviewModal from '../MessageFilePreviewModal';
import useCallbackRef from 'hooks/useCallbackRef';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import { AxiosResponse } from 'axios';
import { FilePreview } from 'models/File';
import { getHasElementsInArray } from 'utils';
import { QAndASides } from 'router/subrouters/DataRoom/models/QAndA';
import useChatFileMenuOptions from '../../hooks/useChatFileMenuOptions';

import './MessageFile.styles.scss';

export type MessageFileHandlers = {
  onFilePreview: (
    fileId: string,
    id?: string,
  ) => Promise<AxiosResponse<FilePreview>>;
  onFileAddToDataRoom?: (fileId: string, id?: string) => Promise<void>;
  onFileRemove?: (
    fileId: string,
    removeFromDataRoom?: boolean,
    id?: string,
  ) => Promise<void>;
  onFileRemoveFromDataRoom?: (fileId: string) => Promise<void>;
};

type MessageFileProps = {
  className?: string;
  id: string;
  name: string;
  disabled?: boolean;
  isDataRoomFile?: boolean;
  canMoveFileToDataRoom?: boolean;
  canRemoveFile?: boolean;
  canRemoveFileFromDataRoom?: boolean;
  dataRoomId: string;
  dataRoomName: string;
  ownerId?: string;
  userSide?: QAndASides;
  /**
   * Hands the preview over to a shared modal that can navigate between files (chat).
   * Without it the file opens in its own single-file modal (notes, nda proof files).
   */
  onOpenPreview?: (fileId: string, fileName: string) => void;
} & Pick<FilePreview, 'type'> &
  Partial<MessageFileHandlers>;

const MessageFile: React.FC<MessageFileProps> = (props) => {
  const {
    className,
    id,
    name,
    isDataRoomFile,
    type,
    disabled,
    canMoveFileToDataRoom,
    canRemoveFile,
    canRemoveFileFromDataRoom,
    dataRoomName,
    dataRoomId,
    ownerId,
    userSide,
    onFilePreview,
    onFileAddToDataRoom,
    onFileRemove,
    onFileRemoveFromDataRoom,
    onOpenPreview,
  } = props;

  const [modal, modalRef] = useCallbackRef<OverlayRef>();

  const { t } = useTranslation();

  const { options: dropdownOptions, isDisabled } = useChatFileMenuOptions({
    id,
    name,
    type,
    disabled,
    isDataRoomFile,
    canMoveFileToDataRoom,
    canRemoveFile,
    canRemoveFileFromDataRoom,
    dataRoomId,
    dataRoomName,
    ownerId,
    userSide,
    onFileAddToDataRoom,
    onFileRemove,
    onFileRemoveFromDataRoom,
  });

  const classes = classNames(
    'message-file',
    { disabled: isDisabled },
    className,
  );

  return (
    <div className={classes}>
      <div
        className={classNames({ clickable: !isDisabled })}
        onClick={() => {
          if (isDisabled) return;

          if (onOpenPreview) {
            onOpenPreview(id, name);
            return;
          }

          modal?.open();
        }}
      >
        <FilePreviewComp type={type} />

        <div className="name">
          <p>{name}</p>
        </div>

        {isDataRoomFile && <p className="tag">{t('dr')}</p>}
      </div>

      {getHasElementsInArray(dropdownOptions) && (
        <MoreDropdown options={dropdownOptions} />
      )}

      {!onOpenPreview && (
        <MessageFilePreviewModal
          ref={modalRef}
          fileId={id}
          fileName={name}
          onFilePreview={onFilePreview}
        />
      )}
    </div>
  );
};

export default MessageFile;
